/**
 * BottomNav — 모바일 전용 하단 네비게이션 (md:hidden)
 * - fixed bottom-0, 높이 64px + safe-area-inset-bottom
 * - 4개 탭: 갤러리, 내 앨범, 요금제, 나
 * - 현재 경로 매치 시 active 스타일 + accent 컬러 underline 슬라이드
 */
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Image, Disc, CreditCard, User } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { isTabActive } from '../../lib/nav-match';
import { motion } from 'framer-motion';

interface NavItem {
  labelKey: string;
  icon: React.ReactNode;
  path: string;
  requiresAuth?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  {
    labelKey: 'header.mobile.gallery',
    icon: <Image size={22} />,
    path: '/gallery',
  },
  {
    labelKey: 'header.mobile.albums',
    icon: <Disc size={22} />,
    path: '/albums',
    requiresAuth: true,
  },
  {
    labelKey: 'header.mobile.pricing',
    icon: <CreditCard size={22} />,
    path: '/pricing',
  },
  {
    labelKey: 'header.mobile.profile',
    icon: <User size={22} />,
    path: '/profile',
  },
];

const BottomNav: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const isActive = (path: string) => isTabActive(path, location.pathname);

  const handleNavClick = (item: NavItem, e: React.MouseEvent) => {
    if (item.requiresAuth && !isAuthenticated) {
      e.preventDefault();
      navigate('/login');
    }
  };

  return (
    <nav
      role="navigation"
      aria-label="하단 네비게이션"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]"
      style={{ '--bottom-nav-height': 'calc(64px + env(safe-area-inset-bottom))', paddingBottom: 'env(safe-area-inset-bottom)' } as React.CSSProperties}
    >
      <div className="flex h-16">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={(e) => handleNavClick(item, e)}
              className={`
                relative flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5
                min-h-[44px] transition-colors
                ${active
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-accent-dark'
                  : 'text-gray-500 dark:text-gray-400 hover:text-accent hover:bg-gray-50 dark:hover:bg-gray-800'
                }
              `}
              aria-current={active ? 'page' : undefined}
            >
              {active && (
                <motion.div
                  layoutId="bottomNavUnderline"
                  className="absolute top-0 left-0 right-0 h-[2px] bg-accent motion-reduce:transition-none"
                />
              )}
              <span className={active ? 'text-accent-dark' : 'text-gray-500'}>
                {item.icon}
              </span>
              <span className="text-[10px] xs:text-xs font-medium leading-none">
                {t(item.labelKey)}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
